/**
 * SMS (#3023): Elisa Dialogi, the fallback channel behind WhatsApp.
 *
 * WhatsApp is the primary channel. A text message is sent only to a customer who
 * does not use WhatsApp, or after WhatsApp confirmed that a message could not be
 * delivered. Elisa Dialogi is the provider OceanSpace chose; it stays OFF until
 * the workshop enters its account's API address and key, confirms that its sender
 * is registered or authorized, checks the configuration and switches SMS on. The
 * API key is write-only and the API never returns it. The address Elisa should
 * deliver replies to carries a secret and is shown exactly once, when it is
 * created. Changing the address, sender or reply route switches SMS off again.
 *
 * The configuration check is local: it makes no request to Elisa. Whether the
 * account, sender and routes really work is verified in the pilot.
 *
 * Mount it as a settings tab behind `settings.edit_workshop`; the tab entry is
 * registered with the other settings tabs (docs/operations/sms-integration.md).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { ApiError } from '@/lib/api';
import { describeApiError } from '@/lib/describeApiError';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useNotify } from '@/hooks/useNotify';
import {
  useDeleteSmsCredentials,
  useDismissSmsReply,
  useResolveSmsMessage,
  useSaveSmsCredentials,
  useSetSmsEnabled,
  useSmsIntegration,
  useSmsReplyContact,
  useTestSmsCredentials,
  type SmsAttentionDecision,
  type SmsAttentionItem,
  type SmsCredentialsInput,
  type SmsCredentialsStatus,
  type SmsReplyContact,
  type SmsSenderType,
  type SmsUnresolvedReply,
} from './useSmsIntegration';

const E164_PATTERN = /^\+[1-9][0-9]{7,14}$/;
const NATIONAL_PATTERN = /^[0-9]{1,15}$/;
const ALPHA_SENDER_PATTERN = /^(?=.*[A-Za-z])[A-Za-z0-9]{1,11}$/;
const API_KEY_PATTERN = /^\S{8,256}$/;
const RECEIVING_PATTERN = /^(\+[1-9][0-9]{4,14}|[0-9]{1,15})$/;

const SENDER_TYPES: readonly SmsSenderType[] = ['MSISDN', 'NATIONAL', 'ALNUM'];

const SENDER_TYPE_KEYS: Record<SmsSenderType, string> = {
  MSISDN: 'senderType.msisdn',
  NATIONAL: 'senderType.national',
  ALNUM: 'senderType.alnum',
};

const ERROR_KEYS: Record<string, string> = {
  SMS_ENCRYPTION_KEY_MISSING: 'errors.encryptionMissing',
  SMS_API_KEY_REQUIRED: 'errors.apiKeyRequired',
  SMS_API_ORIGIN_NOT_ALLOWED: 'errors.apiOriginNotAllowed',
  SMS_CREDENTIALS_MISSING: 'errors.credentialsMissing',
  SMS_NOT_TESTED: 'errors.notTested',
  SMS_PUBLIC_URL_MISSING: 'errors.publicUrlMissing',
  SMS_MESSAGE_NOT_FOUND: 'errors.messageNotFound',
  SMS_REPLY_NOT_FOUND: 'errors.replyNotFound',
};

const LAST_TEST_ERROR_KEYS: Record<string, string> = {
  SMS_CHECK_INCOMPLETE: 'errors.checkIncomplete',
  SMS_CHECK_UNDECRYPTABLE: 'errors.checkUndecryptable',
  SMS_CHECK_ORIGIN_NOT_ALLOWED: 'errors.checkOriginNotAllowed',
  SMS_CHECK_SENDER_NOT_CONFIRMED: 'errors.checkSenderNotConfirmed',
};

const REASON_KEYS: Record<string, string> = {
  unknown_timeout: 'attention.reason.timeout',
  unknown_network: 'attention.reason.network',
  unknown_server_error: 'attention.reason.serverError',
  unknown_rate_limited: 'attention.reason.rateLimited',
  unknown_unexpected_response: 'attention.reason.unexpectedResponse',
  lease_expired_unknown_outcome: 'attention.reason.interrupted',
  sender_converted: 'attention.reason.senderConverted',
  provider_warning: 'attention.reason.providerWarning',
};

const PURPOSE_KEYS: Record<string, string> = {
  booking_notification: 'attention.purpose.bookingNotification',
  offer_link: 'attention.purpose.offerLink',
};

const REPLY_OUTCOME_KEYS: Record<string, string> = {
  unresolved_unknown_text: 'replies.outcome.unknownText',
  unresolved_ambiguous: 'replies.outcome.ambiguous',
  unresolved_stale: 'replies.outcome.stale',
  unresolved_no_open_question: 'replies.outcome.noOpenQuestion',
  unresolved_missing_timestamp: 'replies.outcome.missingTimestamp',
  unresolved_repeat: 'replies.outcome.repeat',
  unresolved_closed: 'replies.outcome.closed',
  unresolved_unsupported: 'replies.outcome.unsupported',
  unresolved_read_only: 'replies.outcome.readOnly',
};

function errorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const data = error.data;
  if (data && typeof data === 'object' && 'code' in data) {
    const code = (data as { code: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

function statusChip(credentials: SmsCredentialsStatus): { color: 'success' | 'warning' | 'error' | 'default'; labelKey: string } {
  if (credentials.ready) return { color: 'success', labelKey: 'status.on' };
  if (credentials.lastTest?.success === false) return { color: 'error', labelKey: 'status.checkFailed' };
  if (credentials.hasApiKey && credentials.lastTest?.success === true) return { color: 'warning', labelKey: 'status.off' };
  if (credentials.hasApiKey) return { color: 'warning', labelKey: 'status.unchecked' };
  return { color: 'default', labelKey: 'status.notConfigured' };
}

function senderValid(type: SmsSenderType, sender: string): boolean {
  if (type === 'MSISDN') return E164_PATTERN.test(sender);
  if (type === 'NATIONAL') return NATIONAL_PATTERN.test(sender);
  return ALPHA_SENDER_PATTERN.test(sender);
}

/**
 * One reply nobody could apply, with what a person needs to follow it up: the work orders (and
 * offers) it may concern, linked, and the customer's full number on request. The number is a
 * separate request behind the customer permission, so the queue itself only ever shows `***567`.
 * Nothing here answers anything for the customer.
 */
function UnresolvedReplyItem({
  item,
  dismissing,
  onDismiss,
  onError,
}: {
  item: SmsUnresolvedReply;
  dismissing: boolean;
  onDismiss: (id: string) => void;
  onError: (error: unknown, fallbackKey: string) => void;
}) {
  const { t } = useTranslation('sms');
  const lookup = useSmsReplyContact();
  const [contact, setContact] = useState<SmsReplyContact | null>(null);

  const showNumber = async () => {
    try {
      setContact(await lookup.mutateAsync(item.id));
    } catch (error) {
      onError(error, 'replies.contactFailed');
    }
  };

  return (
    <ListItem divider alignItems="flex-start" sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
      <ListItemText
        primary={t('replies.item', { sender: item.sender ?? t('replies.unknownSender') })}
        secondary={t(REPLY_OUTCOME_KEYS[item.outcome] ?? 'replies.outcome.other')}
      />
      {item.candidates.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {t('replies.candidates.none')}
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {t('replies.candidates.title')}
          </Typography>
          {item.candidates.map((candidate) => {
            const workOrder = candidate.workOrderNumber ?? candidate.bookingId.slice(0, 8);
            return (
              <Stack key={`${candidate.bookingId}:${candidate.offerId ?? ''}`} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2">
                    {candidate.customerName
                      ? t('replies.candidates.item', { customer: candidate.customerName, workOrder })
                      : t('replies.candidates.itemNoCustomer', { workOrder })}
                  </Typography>
                  {candidate.offerId !== null && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {candidate.offerTitle ? t('replies.candidates.offer', { title: candidate.offerTitle }) : t('replies.candidates.offerUntitled')}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t(candidate.via === 'question' ? 'replies.candidates.viaQuestion' : 'replies.candidates.viaMessage')}
                  </Typography>
                </Box>
                <Button size="small" variant="text" href={`/workorders/${candidate.bookingId}`}>
                  {t('replies.candidates.open')}
                </Button>
              </Stack>
            );
          })}
        </Stack>
      )}
      {contact !== null && (
        <Stack spacing={0.25}>
          {contact.sender !== null && (
            <Typography variant="body2">
              {t('replies.number', { number: contact.sender })}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {contact.customers.length === 0
              ? t('replies.noMatchingCustomer')
              : t('replies.matchingCustomers', { names: contact.customers.map((c) => c.name).join(', ') })}
          </Typography>
        </Stack>
      )}
      <Stack direction="row" spacing={1}>
        {item.sender !== null && contact === null && (
          <Button size="small" variant="outlined" disabled={lookup.isPending} onClick={() => void showNumber()}>
            {t('replies.showNumber')}
          </Button>
        )}
        <Button size="small" variant="outlined" disabled={dismissing} onClick={() => { onDismiss(item.id); }}>
          {t('replies.dismiss')}
        </Button>
      </Stack>
    </ListItem>
  );
}

export function SmsSettingsTab() {
  const { t } = useTranslation('sms');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const query = useSmsIntegration(true);
  const save = useSaveSmsCredentials();
  const test = useTestSmsCredentials();
  const setEnabled = useSetSmsEnabled();
  const remove = useDeleteSmsCredentials();
  const resolve = useResolveSmsMessage();
  const dismiss = useDismissSmsReply();

  const [apiOrigin, setApiOrigin] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [senderType, setSenderType] = useState<SmsSenderType>('MSISDN');
  const [sender, setSender] = useState('');
  const [senderAuthorized, setSenderAuthorized] = useState(false);
  const [receivingNumber, setReceivingNumber] = useState('');
  const [receivingRouteVerified, setReceivingRouteVerified] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  /** The inbound URL with its secret: held only in memory, only until the page is left. */
  const [inboundUrlOnce, setInboundUrlOnce] = useState<string | null>(null);

  useEffect(() => {
    const credentials = query.data?.credentials;
    if (!credentials) return;
    setApiOrigin(credentials.apiOrigin ?? credentials.allowedApiOrigins[0] ?? '');
    setApiKey('');
    setSenderType(credentials.senderType ?? 'MSISDN');
    setSender(credentials.sender ?? '');
    setSenderAuthorized(credentials.senderAuthorized);
    setReceivingNumber(credentials.receivingNumber ?? '');
    setReceivingRouteVerified(credentials.receivingRouteVerified);
  }, [query.data?.credentials]);

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress aria-label={tc('loading')} />
      </Box>
    );
  }
  if (query.isError || !query.data) {
    return <Alert severity="error">{t('loadFailed')}</Alert>;
  }

  const { credentials, publicUrlConfigured, customerPortalOrigin, inboundBaseUrl, attention, attentionCount, unresolvedReplies, unresolvedReplyCount } = query.data;
  const senderTrimmed = sender.trim();
  const senderInvalid = senderTrimmed !== '' && !senderValid(senderType, senderTrimmed);
  const receivingTrimmed = receivingNumber.trim();
  const receivingInvalid = receivingTrimmed !== '' && !RECEIVING_PATTERN.test(receivingTrimmed.replaceAll(/[\s-]/g, ''));
  const keyTrimmed = apiKey.trim();
  const keyInvalid = keyTrimmed !== '' && !API_KEY_PATTERN.test(keyTrimmed);
  const hasKey = credentials.hasApiKey || API_KEY_PATTERN.test(keyTrimmed);
  const noAllowedOrigin = credentials.allowedApiOrigins.length === 0;
  const alnum = senderType === 'ALNUM';
  const routeNeedsNumber = receivingRouteVerified && receivingTrimmed === '';

  const showError = (error: unknown, fallbackKey: string) => {
    const known = ERROR_KEYS[errorCode(error) ?? ''];
    notify.error(tc('error'), known ? t(known) : describeApiError(error, tc, { fallback: t(fallbackKey) }));
  };

  const canSave =
    credentials.encryptionAvailable &&
    apiOrigin !== '' &&
    senderTrimmed !== '' &&
    !senderInvalid &&
    !receivingInvalid &&
    !routeNeedsNumber &&
    hasKey &&
    !keyInvalid &&
    !save.isPending;

  const buildInput = (rotateCallbackToken: boolean): SmsCredentialsInput => {
    const input: SmsCredentialsInput = {
      apiOrigin,
      senderType,
      sender: senderTrimmed,
      senderAuthorized,
      receivingRouteVerified: alnum ? false : receivingRouteVerified,
    };
    if (keyTrimmed !== '') input.apiKey = keyTrimmed;
    if (!alnum && receivingTrimmed !== '') input.receivingNumber = receivingTrimmed;
    if (rotateCallbackToken) input.rotateCallbackToken = true;
    return input;
  };

  const handleSave = async (rotate = false) => {
    if (!canSave) return;
    try {
      const saved = await save.mutateAsync(buildInput(rotate));
      setApiKey('');
      setInboundUrlOnce(saved.inboundUrlOnce);
      notify.success(t('saved'), t('savedDesc'));
    } catch (error) {
      showError(error, 'errors.saveFailed');
    }
  };

  const canTest = credentials.encryptionAvailable && credentials.hasApiKey && credentials.apiOrigin !== null && !test.isPending && !save.isPending;

  const handleTest = async () => {
    if (!canTest) return;
    try {
      const result = await test.mutateAsync(undefined);
      if (result.success) {
        notify.success(t('checkSuccess'), t('checkSuccessDesc'));
      } else {
        const code = result.credentials.lastTest?.error;
        notify.error(tc('error'), t(LAST_TEST_ERROR_KEYS[code ?? ''] ?? 'errors.checkFailed'));
      }
    } catch (error) {
      showError(error, 'errors.checkFailed');
    }
  };

  const canSwitch = credentials.enabled || (credentials.lastTest?.success === true && publicUrlConfigured);

  const handleToggle = async (next: boolean) => {
    try {
      await setEnabled.mutateAsync(next);
      notify.success(next ? t('enabledOn') : t('enabledOff'), next ? t('enabledOnDesc') : t('enabledOffDesc'));
    } catch (error) {
      showError(error, 'errors.toggleFailed');
    }
  };

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(undefined);
      setConfirmDelete(false);
      setInboundUrlOnce(null);
      notify.success(t('deleted'), t('deletedDesc'));
    } catch (error) {
      showError(error, 'errors.deleteFailed');
    }
  };

  const handleRotate = async () => {
    setConfirmRotate(false);
    await handleSave(true);
  };

  const handleResolve = async (id: string, decision: SmsAttentionDecision) => {
    try {
      await resolve.mutateAsync({ id, decision });
      notify.success(t('attention.resolved'), t(`attention.resolvedDesc.${decision}`));
    } catch (error) {
      showError(error, 'errors.resolveFailed');
    }
  };

  const handleDismiss = async (id: string) => {
    try {
      await dismiss.mutateAsync(id);
      notify.success(t('replies.dismissed'), t('replies.dismissedDesc'));
    } catch (error) {
      showError(error, 'errors.dismissFailed');
    }
  };

  const chip = statusChip(credentials);
  const lastTest = credentials.lastTest;

  const actions = (item: SmsAttentionItem) =>
    item.kind === 'sender_warning' ? (
      <Button size="small" variant="outlined" disabled={resolve.isPending} onClick={() => void handleResolve(item.id, 'acknowledge')}>
        {t('attention.acknowledge')}
      </Button>
    ) : (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button size="small" variant="outlined" disabled={resolve.isPending} onClick={() => void handleResolve(item.id, 'sent')}>
          {t('attention.markSent')}
        </Button>
        <Button size="small" variant="outlined" disabled={resolve.isPending} onClick={() => void handleResolve(item.id, 'not_sent')}>
          {t('attention.sendAgain')}
        </Button>
        <Button size="small" color="error" disabled={resolve.isPending} onClick={() => void handleResolve(item.id, 'abandon')}>
          {t('attention.abandon')}
        </Button>
      </Stack>
    );

  return (
    <Stack spacing={3} sx={{ maxWidth: 720 }}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
          <Typography variant="h6">{t('title')}</Typography>
          <Chip size="small" color={chip.color} label={t(chip.labelKey)} />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {t('intro')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('fallbackPolicy')}
        </Typography>
      </Box>

      {!credentials.encryptionAvailable && <Alert severity="warning">{t('encryptionMissing')}</Alert>}
      {!publicUrlConfigured && <Alert severity="info">{t('publicUrlMissing')}</Alert>}
      {/* Booking texts link to the customer's booking in this workshop's own Customer Portal. */}
      {customerPortalOrigin === null ? (
        <Alert severity="warning">{t('portal.missing')}</Alert>
      ) : (
        <Alert severity="info">{t('portal.configured', { origin: customerPortalOrigin })}</Alert>
      )}
      {noAllowedOrigin && <Alert severity="warning">{t('noAllowedOrigin')}</Alert>}
      {credentials.apiOrigin !== null && !credentials.apiOriginAllowed && <Alert severity="warning">{t('originNoLongerAllowed')}</Alert>}
      {lastTest?.success === false && <Alert severity="error">{t(LAST_TEST_ERROR_KEYS[lastTest.error ?? ''] ?? 'errors.checkFailed')}</Alert>}
      {credentials.ready && <Alert severity="success">{t('readyHint')}</Alert>}

      <Stack spacing={2}>
        <TextField
          select
          label={t('apiOrigin')}
          value={apiOrigin}
          onChange={(e) => { setApiOrigin(e.target.value); }}
          helperText={t('apiOriginHint')}
          disabled={noAllowedOrigin}
          fullWidth
        >
          {credentials.allowedApiOrigins.map((origin) => (
            <MenuItem key={origin} value={origin}>
              {origin}
            </MenuItem>
          ))}
          {apiOrigin !== '' && !credentials.allowedApiOrigins.includes(apiOrigin) && (
            <MenuItem value={apiOrigin} disabled>
              {apiOrigin}
            </MenuItem>
          )}
        </TextField>
        <TextField
          label={t('apiKey')}
          type="password"
          value={apiKey}
          onChange={(e) => { setApiKey(e.target.value); }}
          error={keyInvalid}
          helperText={keyInvalid ? t('apiKeyInvalid') : credentials.hasApiKey ? t('apiKeySetHint') : t('apiKeyHint')}
          autoComplete="new-password"
          fullWidth
        />
        <TextField select label={t('senderTypeLabel')} value={senderType} onChange={(e) => { setSenderType(e.target.value as SmsSenderType); }} fullWidth>
          {SENDER_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {t(SENDER_TYPE_KEYS[type])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('sender')}
          value={sender}
          onChange={(e) => { setSender(e.target.value); }}
          error={senderInvalid}
          helperText={senderInvalid ? t(`senderInvalid.${senderType}`) : t(`senderHint.${senderType}`)}
          autoComplete="off"
          slotProps={{ htmlInput: { maxLength: alnum ? 11 : 30 } }}
          fullWidth
        />
        <FormControlLabel
          control={<Checkbox checked={senderAuthorized} onChange={(_event, checked) => { setSenderAuthorized(checked); }} />}
          label={t('senderAuthorized')}
        />
        <Typography variant="caption" color="text.secondary">
          {t('senderAuthorizedHint')}
        </Typography>

        {alnum ? (
          <Alert severity="info">{t('alnumNote')}</Alert>
        ) : (
          <>
            <TextField
              label={t('receivingNumber')}
              value={receivingNumber}
              onChange={(e) => { setReceivingNumber(e.target.value); }}
              error={receivingInvalid}
              helperText={receivingInvalid ? t('receivingNumberInvalid') : t('receivingNumberHint')}
              autoComplete="off"
              fullWidth
            />
            <FormControlLabel
              control={<Checkbox checked={receivingRouteVerified} onChange={(_event, checked) => { setReceivingRouteVerified(checked); }} />}
              label={t('receivingRouteVerified')}
            />
            <Typography variant="caption" color="text.secondary">
              {t('receivingRouteHint')}
            </Typography>
          </>
        )}
        <Alert severity={credentials.repliesEnabled ? 'success' : 'info'}>
          {credentials.repliesEnabled ? t('repliesOn') : t('repliesOff')}
        </Alert>

        {inboundUrlOnce !== null && (
          <Alert severity="warning">
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('inboundUrlOnceHint')}
            </Typography>
            <TextField
              label={t('inboundUrl')}
              value={inboundUrlOnce}
              slotProps={{ input: { readOnly: true } }}
              fullWidth
              size="small"
            />
          </Alert>
        )}
        {inboundUrlOnce === null && inboundBaseUrl !== null && !alnum && (
          <TextField
            label={t('inboundBaseUrl')}
            value={inboundBaseUrl}
            helperText={credentials.hasCallbackToken ? t('inboundBaseUrlHint') : t('inboundBaseUrlHintNew')}
            slotProps={{ input: { readOnly: true } }}
            fullWidth
          />
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            startIcon={save.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => void handleSave()}
            disabled={!canSave}
          >
            {t('save')}
          </Button>
          <Button
            variant="outlined"
            startIcon={test.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => void handleTest()}
            disabled={!canTest}
          >
            {t('check')}
          </Button>
          <Button variant="outlined" onClick={() => { setConfirmRotate(true); }} disabled={!credentials.hasCallbackToken || !canSave}>
            {t('rotateCallback')}
          </Button>
          <Button color="error" onClick={() => { setConfirmDelete(true); }} disabled={!credentials.hasApiKey || remove.isPending}>
            {t('delete')}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t('checkHint')}
        </Typography>
      </Stack>

      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={credentials.enabled}
              onChange={(_event, checked) => void handleToggle(checked)}
              disabled={!canSwitch || setEnabled.isPending}
            />
          }
          label={t('enabledLabel')}
        />
        <Typography variant="caption" color="text.secondary" component="p">
          {credentials.lastTest?.success === true ? t('enabledHint') : t('enabledNeedsCheck')}
        </Typography>
      </Box>

      {attentionCount > 0 && (
        <Stack spacing={1}>
          <Typography variant="subtitle1">{t('attention.title', { count: attentionCount })}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('attention.intro')}
          </Typography>
          <List dense disablePadding>
            {attention.map((item) => (
              <ListItem key={item.id} divider alignItems="flex-start" sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                <ListItemText
                  primary={t('attention.item', {
                    purpose: t(PURPOSE_KEYS[item.purpose] ?? 'attention.purpose.other'),
                    recipient: item.recipient,
                  })}
                  secondary={t(REASON_KEYS[item.reason ?? ''] ?? 'attention.reason.unknown')}
                />
                {actions(item)}
              </ListItem>
            ))}
          </List>
        </Stack>
      )}

      {unresolvedReplyCount > 0 && (
        <Stack spacing={1}>
          <Typography variant="subtitle1">{t('replies.title', { count: unresolvedReplyCount })}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('replies.intro')}
          </Typography>
          <List dense disablePadding>
            {unresolvedReplies.map((item) => (
              <UnresolvedReplyItem
                key={item.id}
                item={item}
                dismissing={dismiss.isPending}
                onDismiss={(id) => void handleDismiss(id)}
                onError={showError}
              />
            ))}
          </List>
        </Stack>
      )}

      <ConfirmDialog
        open={confirmRotate}
        title={t('rotateTitle')}
        message={t('rotateMessage')}
        confirmText={t('rotateCallback')}
        onConfirm={() => void handleRotate()}
        onCancel={() => { setConfirmRotate(false); }}
        loading={save.isPending}
      />
      <ConfirmDialog
        open={confirmDelete}
        title={t('deleteTitle')}
        message={t('deleteMessage')}
        confirmColor="error"
        confirmText={tc('delete')}
        onConfirm={() => void handleDelete()}
        onCancel={() => { setConfirmDelete(false); }}
        loading={remove.isPending}
      />
    </Stack>
  );
}
